import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

/**
 * Custom hook to fetch qualifications page data using optimized RPC
 * Includes server-side pagination, filtering, and live stats calculation
 *
 * @param {string} companyId - The company ID
 * @param {object} options - Configuration options
 * @param {string} options.projectId - Optional project ID filter
 * @param {string} options.search - Search query
 * @param {string} options.priority - Priority filter ('all', 'high', 'medium', 'low')
 * @param {number} options.page - Current page
 * @param {number} options.limit - Items per page
 * @param {boolean} options.enabled - Whether to fetch data
 * @returns {object} { qualifications, projects, summary, pagination, loading, error, refetch }
 */
export const useQualificationsData = (companyId, options = {}) => {
  const {
    projectId = null,
    search = '',
    priority = 'all',
    page = 1,
    limit = 100,
    enabled = true
  } = options;

  const [qualifications, setQualifications] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState({
    totalQualifications: 0,
    activeProjects: 0,
    highPriority: 0,
    categories: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    totalCount: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const debounceTimerRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search]);

  const fetchData = useCallback(async () => {
    if (!companyId || !enabled) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.rpc('get_qualifications_page_data', {
        p_company_id: companyId,
        p_project_id: projectId || null,
        p_search: debouncedSearch || null,
        p_priority: priority,
        p_page: page,
        p_limit: limit
      });

      if (fetchError) throw fetchError;

      // Filter out documents and checklists - only show actual qualifications/courses
      const filteredQualifications = (data.qualifications || []).filter(
        q => q.requirement_type !== 'documentation' && q.requirement_type !== 'internal_checklist'
      );

      setQualifications(filteredQualifications);
      setProjects(data.projects || []);
      setSummary(data.summary || {
        totalQualifications: 0,
        activeProjects: 0,
        highPriority: 0,
        categories: 0
      });
      setPagination(data.pagination || {
        page: 1,
        limit: 100,
        totalCount: 0,
        totalPages: 0
      });
    } catch (err) {
      console.error('Error fetching qualifications data:', err);
      setError(err);
      toast.error('Failed to load qualifications');
    } finally {
      setLoading(false);
    }
  }, [companyId, projectId, debouncedSearch, priority, page, limit, enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Group qualifications by project
  const qualificationsByProject = qualifications.reduce((acc, qual) => {
    const projectName = qual.projects?.name || 'No Project';
    if (!acc[projectName]) {
      acc[projectName] = [];
    }
    acc[projectName].push(qual);
    return acc;
  }, {});

  return {
    qualifications,
    qualificationsByProject,
    projects,
    summary,
    pagination,
    loading,
    error,
    refetch: fetchData
  };
};
